/* ============================================
   Weekly Deal — Countdown Timer Web Component
   ============================================ */

if (!customElements.get('weekly-deal-countdown')) {
  class WeeklyDealCountdown extends HTMLElement {
    connectedCallback() {
      this.targetDate = this.calculateTargetDate();
      if (!this.targetDate) return;

      this.daysEl = this.querySelector('[data-days]');
      this.hoursEl = this.querySelector('[data-hours]');
      this.minutesEl = this.querySelector('[data-minutes]');
      this.secondsEl = this.querySelector('[data-seconds]');
      this.expiredMsg = this.dataset.expiredText || 'Süre doldu';

      this.update();
      this.interval = setInterval(() => this.update(), 1000);
    }

    disconnectedCallback() {
      if (this.interval) clearInterval(this.interval);
    }

    calculateTargetDate() {
      var dateStr = this.dataset.date;
      var timeStr = this.dataset.time || '00:00';
      if (!dateStr) return null;

      var d = new Date(dateStr + 'T' + timeStr + ':00');
      if (isNaN(d.getTime())) return null;
      return d;
    }

    update() {
      var now = new Date();
      var diff = this.targetDate - now;

      if (diff <= 0) {
        clearInterval(this.interval);
        if (this.daysEl) this.daysEl.textContent = '0';
        if (this.hoursEl) this.hoursEl.textContent = '00';
        if (this.minutesEl) this.minutesEl.textContent = '00';
        if (this.secondsEl) this.secondsEl.textContent = '00';
        return;
      }

      var days = Math.floor(diff / 86400000);
      var hours = Math.floor((diff % 86400000) / 3600000);
      var minutes = Math.floor((diff % 3600000) / 60000);
      var seconds = Math.floor((diff % 60000) / 1000);

      if (this.daysEl) this.daysEl.textContent = String(days);
      if (this.hoursEl) this.hoursEl.textContent = String(hours).padStart(2, '0');
      if (this.minutesEl) this.minutesEl.textContent = String(minutes).padStart(2, '0');
      if (this.secondsEl) this.secondsEl.textContent = String(seconds).padStart(2, '0');
    }
  }

  customElements.define('weekly-deal-countdown', WeeklyDealCountdown);
}

/* ============================================
   Weekly Deal — Banner Slider (tek banner görünümü)
   Kaydırma native scroll-snap; bu bileşen oklar, noktalar ve otomatik geçişi yönetir.
   Otomatik geçiş: bölüm görünür, sekme açık ve kullanıcı fare/odak/dokunmayla
   bannerla etkileşimde değilken çalışır.
   ============================================ */

if (!customElements.get('weekly-deal-banners')) {
  class WeeklyDealBanners extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-wd-banner-track]');
      this.slides = Array.prototype.slice.call(this.querySelectorAll('[data-wd-banner-slide]'));
      if (!this.track || this.slides.length < 2) return;

      this.dots = Array.prototype.slice.call(this.querySelectorAll('[data-wd-banner-dot]'));
      this.index = 0;
      this.holds = { hover: false, focus: false, touch: false, editor: false };
      this.visible = false;
      this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.autoplay = this.dataset.autoplay === 'true' && !this.reduceMotion;
      this.speed = (parseInt(this.dataset.speed, 10) || 5) * 1000;

      this.track.addEventListener('scroll', () => this.onScroll(), { passive: true });

      var prevBtn = this.querySelector('[data-wd-banner-prev]');
      var nextBtn = this.querySelector('[data-wd-banner-next]');
      if (prevBtn) prevBtn.addEventListener('click', () => this.goTo(this.index - 1));
      if (nextBtn) nextBtn.addEventListener('click', () => this.goTo(this.index + 1));
      this.dots.forEach((dot, i) => {
        dot.addEventListener('click', () => this.goTo(i));
      });

      this.addEventListener('mouseenter', () => this.hold('hover', true));
      this.addEventListener('mouseleave', () => this.hold('hover', false));
      this.addEventListener('focusin', () => this.hold('focus', true));
      this.addEventListener('focusout', (e) => {
        if (!this.contains(e.relatedTarget)) this.hold('focus', false);
      });
      this.track.addEventListener('touchstart', () => this.hold('touch', true), { passive: true });
      this.track.addEventListener('touchend', () => this.hold('touch', false), { passive: true });

      /* Tema editörü: seçilen banner bloğuna git, seçiliyken dönmesin */
      this.addEventListener('shopify:block:select', (e) => {
        var i = this.slides.indexOf(e.target.closest('[data-wd-banner-slide]'));
        if (i > -1) this.goTo(i);
        this.hold('editor', true);
      });
      this.addEventListener('shopify:block:deselect', () => this.hold('editor', false));

      this.onVisibility = () => this.schedule();
      document.addEventListener('visibilitychange', this.onVisibility);

      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver((entries) => {
          this.visible = entries[0].isIntersecting;
          if (this.visible) this.loadImages();
          this.schedule();
        }, { rootMargin: '200px 0px' });
        this.observer.observe(this);
      } else {
        this.visible = true;
        this.loadImages();
        this.schedule();
      }
    }

    disconnectedCallback() {
      this.stop();
      if (this.observer) this.observer.disconnect();
      if (this.onVisibility) document.removeEventListener('visibilitychange', this.onVisibility);
    }

    /* Yatay kaydırıcıdaki lazy görseller kaydırılana kadar inmeyebilir; bölüm yaklaşınca hepsini başlat */
    loadImages() {
      if (this.imagesLoaded) return;
      this.imagesLoaded = true;
      this.querySelectorAll('img[loading="lazy"]').forEach((img) => {
        img.loading = 'eager';
      });
    }

    goTo(index) {
      var count = this.slides.length;
      var target = ((index % count) + count) % count;
      this.track.scrollTo({
        left: target * this.track.clientWidth,
        behavior: this.reduceMotion ? 'auto' : 'smooth'
      });
      this.setIndex(target);
      this.schedule();
    }

    onScroll() {
      if (this.scrollFrame) return;
      this.scrollFrame = requestAnimationFrame(() => {
        this.scrollFrame = null;
        var width = this.track.clientWidth || 1;
        this.setIndex(Math.round(this.track.scrollLeft / width));
      });
    }

    setIndex(index) {
      if (index < 0 || index >= this.slides.length) return;
      this.index = index;
      this.dots.forEach((dot, i) => {
        if (i === index) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
    }

    hold(reason, active) {
      this.holds[reason] = active;
      this.schedule();
    }

    schedule() {
      this.stop();
      if (!this.autoplay || !this.visible || document.hidden) return;
      var h = this.holds;
      if (h.hover || h.focus || h.touch || h.editor) return;
      this.timer = setTimeout(() => this.goTo(this.index + 1), this.speed);
    }

    stop() {
      if (this.timer) {
        clearTimeout(this.timer);
        this.timer = null;
      }
    }
  }

  customElements.define('weekly-deal-banners', WeeklyDealBanners);
}

/* ============================================
   Weekly Deal — Kampanya modu (MyTheme Toolkit "Haftanın Fırsatları")
   --------------------------------------------
   Periyot verisi section'daki data-window-* (shop metafield state, ISO UTC).
   Fazlar: upcoming (windowStart öncesi) → live → ended (followingWindowStart'a
   geri sayım) → over (güncel bölüm Section Rendering API ile yeniden çekilir).
   Sunucu saati + bayat HTML tespiti: GET /apps/wishlist/weekly-deals/state
   Kupon (yalnızca periyot içinde verilir): GET /apps/wishlist/weekly-deals/coupon
   ============================================ */

if (!customElements.get('weekly-deal-campaign')) {
  var WD_STATE_URL = '/apps/wishlist/weekly-deals/state';
  var WD_COUPON_URL = '/apps/wishlist/weekly-deals/coupon';
  var WD_TIME_ZONE = 'Europe/Istanbul';
  var WD_DAY_MS = 86400000;
  /* sectionId → yeniden çekme zamanları (döngüye girmemek için) */
  var wdRefreshLog = {};

  var wdParseTime = function (value) {
    if (!value) return null;
    var t = Date.parse(value);
    return isNaN(t) ? null : t;
  };

  var wdDayKey = function (ts) {
    return new Date(ts).toLocaleDateString('en-CA', { timeZone: WD_TIME_ZONE });
  };

  class WeeklyDealCampaign extends HTMLElement {
    connectedCallback() {
      if (!this.initialized) this.init();
      this.tick();
      if (!this.interval) this.interval = setInterval(() => this.tick(), 1000);
    }

    disconnectedCallback() {
      if (this.interval) clearInterval(this.interval);
      this.interval = null;
    }

    init() {
      this.initialized = true;
      this.sectionId = this.dataset.sectionId || '';
      this.windowId = this.dataset.windowId || '';
      this.windowStart = wdParseTime(this.dataset.windowStart);
      this.windowEnd = wdParseTime(this.dataset.windowEnd);
      this.followingStart = wdParseTime(this.dataset.followingStart);
      /* Ürün değişimli periyotta kuralın bitişi; yoksa windowEnd */
      this.periodEnd = wdParseTime(this.dataset.periodEnd) || this.windowEnd;
      this.preview = this.hasAttribute('data-preview');
      this.locale = document.documentElement.lang || 'tr';
      this.offset = 0;
      this.phase = null;
      this.couponRequest = 0;
      this.i18n = this.readI18n();

      this.phaseEl = this.querySelector('[data-wd-phase]');
      this.countdownEl = this.querySelector('[data-wd-countdown]');
      this.daysItem = this.querySelector('[data-wd-days-item]');
      this.daysEl = this.querySelector('[data-days]');
      this.hoursEl = this.querySelector('[data-hours]');
      this.minutesEl = this.querySelector('[data-minutes]');
      this.secondsEl = this.querySelector('[data-seconds]');
      this.nextEl = this.querySelector('[data-wd-next]');
      this.waitingEl = this.querySelector('[data-wd-waiting]');
      this.liveEl = this.querySelector('[data-wd-live]');

      this.dialog = this.querySelector('[data-wd-dialog]');
      if (this.dialog) {
        this.dialogProduct = this.dialog.querySelector('[data-wd-dialog-product]');
        this.statusEl = this.dialog.querySelector('[data-wd-dialog-status]');
        this.successEl = this.dialog.querySelector('[data-wd-dialog-success]');
        this.codeEl = this.dialog.querySelector('[data-wd-code]');
        this.copyBtn = this.dialog.querySelector('[data-wd-copy]');
        this.metaEl = this.dialog.querySelector('[data-wd-meta]');
        this.applyBtn = this.dialog.querySelector('[data-wd-apply]');
        this.loginLink = this.dialog.querySelector('[data-wd-login]');
        /* Arka plana (backdrop) tıklayınca kapat */
        this.dialog.addEventListener('click', (e) => {
          if (e.target === this.dialog) this.closeDialog();
        });
        this.dialog.addEventListener('close', () => {
          if (this.activeButton && document.contains(this.activeButton)) this.activeButton.focus();
        });
      }

      this.addEventListener('click', (e) => this.onClick(e));
      this.syncState();
    }

    readI18n() {
      var el = this.querySelector('[data-wd-i18n]');
      var data = {};
      if (el) {
        try { data = JSON.parse(el.textContent) || {}; } catch (e) { data = {}; }
      }
      return data;
    }

    t(key, fallback) {
      return this.i18n[key] || fallback || '';
    }

    now() {
      return Date.now() + this.offset;
    }

    /* ---------------- Faz + geri sayım ---------------- */

    tick() {
      var now = this.now();
      var phase;
      var target = null;
      if (this.windowStart !== null && now < this.windowStart) {
        phase = 'upcoming';
        target = this.windowStart;
      } else if (this.windowEnd !== null && now < this.windowEnd) {
        phase = 'live';
        target = this.windowEnd;
      } else if (this.isRotating()) {
        /* Ardışık alt periyot (ürün değişimi): sunucu yeni ürünleri birkaç sn içinde yayımlar */
        phase = 'switching';
      } else if (this.followingStart !== null && now < this.followingStart) {
        phase = 'ended';
        target = this.followingStart;
      } else {
        phase = 'over';
      }

      if (phase !== this.phase) {
        this.setPhase(phase, target);
        if (phase === 'switching') this.waitForNextWindow();
      }
      this.renderCountdown(target === null ? 0 : target - now);
      /* Sıradaki periyodun ürünleri bu HTML'de yok → güncel bölümü çek (throttle'lı) */
      if (phase === 'over') this.refreshSection();
    }

    setPhase(phase, target) {
      this.phase = phase;
      this.dataset.phase = phase;
      var live = phase === 'live';
      var switching = phase === 'switching';
      var rotating = this.isRotating();
      /* Değişim anında eski ürünler yeni HTML gelene kadar görünür kalır (boş kutu yanıp sönmesin) */
      var showProducts = live || switching || this.preview;

      if (this.liveEl) this.liveEl.hidden = !showProducts;
      if (this.waitingEl) this.waitingEl.hidden = showProducts;
      if (this.countdownEl) this.countdownEl.hidden = phase === 'over' || switching;

      if (this.phaseEl) {
        if (phase === 'over') this.phaseEl.textContent = this.t('campaignOver');
        else if (switching) this.phaseEl.textContent = this.t('switching');
        else if (live) this.phaseEl.textContent = rotating ? this.t('changesIn') : this.t('endsIn');
        else this.phaseEl.textContent = this.t('startsIn');
      }

      if (this.nextEl) {
        if ((phase === 'upcoming' || phase === 'ended') && target !== null) {
          this.nextEl.textContent = this.t('nextStart', '%when%').replace('%when%', this.formatWhen(target));
          this.nextEl.hidden = false;
        } else if (live && rotating && this.periodEnd !== null && this.periodEnd > this.windowEnd) {
          this.nextEl.textContent = this.t('periodEnds', '%time%').replace('%time%', this.formatTime(this.periodEnd));
          this.nextEl.hidden = false;
        } else {
          this.nextEl.hidden = true;
        }
      }
    }

    renderCountdown(diff) {
      if (diff < 0) diff = 0;
      var days = Math.floor(diff / WD_DAY_MS);
      var hours = Math.floor((diff % WD_DAY_MS) / 3600000);
      var minutes = Math.floor((diff % 3600000) / 60000);
      var seconds = Math.floor((diff % 60000) / 1000);
      if (this.daysEl) this.daysEl.textContent = String(days);
      if (this.daysItem) this.daysItem.hidden = days === 0;
      if (this.hoursEl) this.hoursEl.textContent = String(hours).padStart(2, '0');
      if (this.minutesEl) this.minutesEl.textContent = String(minutes).padStart(2, '0');
      if (this.secondsEl) this.secondsEl.textContent = String(seconds).padStart(2, '0');
    }

    formatTime(ts) {
      return new Date(ts).toLocaleTimeString(this.locale, {
        hour: '2-digit', minute: '2-digit', timeZone: WD_TIME_ZONE
      });
    }

    /* "Bugün 15:00" / "Yarın 15:00" / "4 Ekim Cumartesi 15:00" (İstanbul saati) */
    formatWhen(ts) {
      var now = this.now();
      var key = wdDayKey(ts);
      var label;
      if (key === wdDayKey(now)) {
        label = this.t('today');
      } else if (key === wdDayKey(now + WD_DAY_MS)) {
        label = this.t('tomorrow');
      } else {
        label = new Date(ts).toLocaleDateString(this.locale, {
          weekday: 'long', day: 'numeric', month: 'long', timeZone: WD_TIME_ZONE
        });
      }
      return (label + ' ' + this.formatTime(ts)).trim();
    }

    /* ---------------- Sunucu saati + bayat HTML ---------------- */

    syncState() {
      var sentAt = Date.now();
      fetch(WD_STATE_URL, {
        headers: { Accept: 'application/json' },
        cache: 'no-store',
        credentials: 'same-origin'
      })
        .then((res) => (res.ok ? res.json() : null))
        .then((data) => {
          if (!data || !data.ok) return;
          var serverNow = wdParseTime(data.serverNow);
          if (serverNow !== null) {
            /* İstek gidiş-dönüşünün ortasını sunucu anı say */
            this.offset = serverNow - Math.round((sentAt + Date.now()) / 2);
            this.tick();
          }
          var serverWindowId = data.campaignId ? data.windowId || '' : '';
          if (serverWindowId !== this.windowId) this.refreshSection();
        })
        .catch(function () {});
    }

    /* Sıradaki alt periyot bu periyodun hemen ardından mı (ürün değişimi / bitişik periyot)? */
    isRotating() {
      return this.windowEnd !== null && this.followingStart !== null &&
        Math.abs(this.followingStart - this.windowEnd) < 1000;
    }

    /* Ürün değişimi: /state'i 3 sn arayla yokla, yeni windowId gelince bölümü hemen yeniden çek (en fazla ~2 dk) */
    waitForNextWindow() {
      if (this.preview || this.switchPolling) return;
      this.switchPolling = true;
      var tries = 0;
      var poll = () => {
        if (!this.isConnected || this.phase !== 'switching') {
          this.switchPolling = false;
          return;
        }
        fetch(WD_STATE_URL, { headers: { Accept: 'application/json' }, cache: 'no-store', credentials: 'same-origin' })
          .then((res) => (res.ok ? res.json() : null))
          .then((data) => {
            var serverWindowId = data && data.ok ? (data.campaignId ? data.windowId || '' : '') : null;
            if (serverWindowId !== null && serverWindowId !== this.windowId) {
              this.switchPolling = false;
              this.refreshSection(true);
            } else if (++tries < 40) {
              setTimeout(poll, 3000);
            } else {
              this.switchPolling = false;
              this.refreshSection();
            }
          })
          .catch(() => {
            if (++tries < 40) setTimeout(poll, 3000);
            else this.switchPolling = false;
          });
      };
      setTimeout(poll, 1500);
    }

    refreshSection(force) {
      if (this.preview || !this.sectionId || this.refreshing) return;
      /* Kupon popup'ı açıkken müşterinin koduna dokunma; kapanınca yenile */
      if (this.dialog && this.dialog.open) {
        if (!this.refreshAfterClose) {
          this.refreshAfterClose = true;
          this.dialog.addEventListener('close', () => {
            this.refreshAfterClose = false;
            this.refreshSection(force);
          }, { once: true });
        }
        return;
      }
      var now = Date.now();
      var log = wdRefreshLog[this.sectionId] || (wdRefreshLog[this.sectionId] = []);
      while (log.length && now - log[0] > 600000) log.shift();
      /* En az 30 sn arayla (sunucu yeni windowId'yi doğruladıysa — force — beklemeden), 10 dakikada en fazla 6 deneme */
      if (!force && log.length && now - log[log.length - 1] < 30000) return;
      if (log.length >= 6) return;
      log.push(now);

      this.refreshing = true;
      var sectionId = this.sectionId;
      var url = window.location.pathname + '?sections=' + encodeURIComponent(sectionId) + '&_wd=' + now;
      fetch(url, { headers: { Accept: 'application/json' } })
        .then((res) => (res.ok ? res.json() : null))
        .then((data) => {
          var html = data && data[sectionId];
          var wrapper = document.getElementById('shopify-section-' + sectionId);
          if (!html || !wrapper) return;
          var tmp = document.createElement('div');
          tmp.innerHTML = html;
          /* Kampanya bölümün yalnızca bir parçasıysa (ör. Hero paneli) yalnız onu değiştir;
             bölümün geri kalanı (slider) yerinde kalsın. Yeni HTML'de kampanya yoksa paneli kaldır. */
          if (this.hasAttribute('data-partial-refresh')) {
            if (!this.isConnected) return;
            var freshCampaign = tmp.querySelector('weekly-deal-campaign');
            if (freshCampaign) this.replaceWith(freshCampaign);
            else this.remove();
            return;
          }
          var fresh = tmp.querySelector('#shopify-section-' + sectionId);
          wrapper.innerHTML = fresh ? fresh.innerHTML : html;
        })
        .catch(function () {})
        .then(() => {
          this.refreshing = false;
        });
    }

    /* ---------------- Kupon popup ---------------- */

    onClick(e) {
      var couponBtn = e.target.closest('[data-deal-coupon]');
      if (couponBtn && this.contains(couponBtn)) {
        e.preventDefault();
        this.openCoupon(couponBtn);
        return;
      }
      if (!this.dialog || !this.dialog.contains(e.target)) return;
      if (e.target.closest('[data-wd-close]')) this.closeDialog();
      else if (e.target.closest('[data-wd-copy]')) this.copyCode();
      else if (e.target.closest('[data-wd-apply]')) this.applyCoupon();
    }

    openCoupon(button) {
      if (!this.dialog) return;
      this.activeButton = button;
      this.activeVariantId = button.dataset.variantId || '';
      this.activeCode = null;

      if (this.dialogProduct) this.dialogProduct.textContent = button.dataset.productTitle || '';
      if (this.successEl) this.successEl.hidden = true;
      if (this.loginLink) this.loginLink.hidden = true;
      if (this.codeEl) this.codeEl.textContent = '';
      this.setStatus(this.t('loading'), false);
      this.showDialog();

      var requestId = ++this.couponRequest;
      var url = WD_COUPON_URL + '?productId=' + encodeURIComponent(button.dataset.productId || '');
      fetch(url, {
        headers: { Accept: 'application/json' },
        cache: 'no-store',
        credentials: 'same-origin'
      })
        .then((res) => (res.ok ? res.json() : null))
        .then((data) => {
          if (requestId !== this.couponRequest) return;
          if (data && data.ok && data.code) this.showCode(data);
          else this.showError(data && data.reason);
        })
        .catch(() => {
          if (requestId === this.couponRequest) this.showError('error');
        });
    }

    showCode(data) {
      this.activeCode = String(data.code);
      this.setStatus('', false);
      if (this.codeEl) this.codeEl.textContent = this.activeCode;

      /* Kodun kendi indirimi: "%20" ya da "50 TL" (uygulama label'ı hazır verir).
         Eski yanıtta discount yok → percent'e düşer. */
      var discountText = data.discount && data.discount.label
        ? String(data.discount.label)
        : '%' + (data.percent != null ? data.percent : this.dataset.percent);
      var endTs = wdParseTime(data.windowEnd) || this.windowEnd;
      /* Çeviride % işareti {{ }}'ye bitişik olunca Shopify interpolasyonu bozuluyor;
         bu yüzden indirim metni JS'te birleştirilir */
      if (this.metaEl) {
        this.metaEl.textContent = endTs
          ? this.t('couponMeta').replace('%discount%', discountText).replace('%time%', this.formatTime(endTs))
          : '';
      }
      if (this.copyBtn) {
        this.copyBtn.textContent = this.t('copy');
        this.copyBtn.classList.remove('is-copied');
      }
      if (this.applyBtn) {
        this.applyBtn.hidden = !this.activeVariantId;
        this.applyBtn.disabled = false;
        this.applyBtn.textContent = this.t('apply');
      }
      if (this.successEl) this.successEl.hidden = false;
      if (this.copyBtn) this.copyBtn.focus();
    }

    showError(reason) {
      var messages = {
        no_campaign: this.t('errorNotLive'),
        not_live: this.t('errorNotLive'),
        not_in_deal: this.t('errorNotInDeal'),
        login_required: this.t('errorLoginRequired'),
        rate_limited: this.t('errorRateLimited')
      };
      this.setStatus(messages[reason] || this.t('errorGeneric'), true);
      if (this.loginLink) this.loginLink.hidden = reason !== 'login_required';
      /* Sayfadaki periyot bilgisi bayat olabilir */
      if (reason === 'not_live' || reason === 'no_campaign' || reason === 'not_in_deal') this.syncState();
    }

    setStatus(message, isError) {
      if (!this.statusEl) return;
      this.statusEl.textContent = message || '';
      this.statusEl.hidden = !message;
      this.statusEl.classList.toggle('is-error', !!isError);
    }

    showDialog() {
      if (typeof this.dialog.showModal === 'function') {
        if (!this.dialog.open) this.dialog.showModal();
      } else {
        this.dialog.setAttribute('open', '');
      }
    }

    closeDialog() {
      if (!this.dialog) return;
      this.couponRequest++;
      if (typeof this.dialog.close === 'function') this.dialog.close();
      else this.dialog.removeAttribute('open');
    }

    copyCode() {
      var code = this.activeCode;
      if (!code || !this.copyBtn) return;
      var done = () => {
        this.copyBtn.textContent = this.t('copied');
        this.copyBtn.classList.add('is-copied');
        clearTimeout(this.copyTimer);
        this.copyTimer = setTimeout(() => {
          this.copyBtn.textContent = this.t('copy');
          this.copyBtn.classList.remove('is-copied');
        }, 2000);
      };
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(code).then(done, () => {
          if (this.copyFallback(code)) done();
        });
      } else if (this.copyFallback(code)) {
        done();
      }
    }

    /* Modal dialog top-layer'da olduğu için geçici alan dialog'un içine eklenir */
    copyFallback(code) {
      var area = document.createElement('textarea');
      area.value = code;
      area.setAttribute('readonly', '');
      area.className = 'visually-hidden';
      (this.dialog || document.body).appendChild(area);
      area.select();
      var ok = false;
      try { ok = document.execCommand('copy'); } catch (e) { ok = false; }
      area.remove();
      return ok;
    }

    /* Ürünü sepete ekler, ardından kodu /discount/ ile uygulayıp sepete yönlendirir */
    applyCoupon() {
      var code = this.activeCode;
      var variantId = this.activeVariantId;
      if (!code || !variantId || !this.applyBtn) return;
      var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      var cartUrl = this.dataset.cartUrl || root + 'cart';

      this.applyBtn.disabled = true;
      this.applyBtn.textContent = this.t('applying');
      fetch(root + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] })
      })
        .then((res) => {
          if (!res.ok) throw new Error('cart add failed: ' + res.status);
          window.location.href = root + 'discount/' + encodeURIComponent(code) +
            '?redirect=' + encodeURIComponent(cartUrl);
        })
        .catch(() => {
          this.applyBtn.disabled = false;
          this.applyBtn.textContent = this.t('apply');
          this.setStatus(this.t('errorAddToCart'), true);
        });
    }
  }

  customElements.define('weekly-deal-campaign', WeeklyDealCampaign);
}

/* Shopify editor support */
document.addEventListener('shopify:section:load', function(event) {
  var countdown = event.target.querySelector('weekly-deal-countdown');
  if (countdown && countdown.connectedCallback) {
    countdown.connectedCallback();
  }
});
